import type { ReactElement } from "react"

import type { ChatProps } from "./routes.types"

import { AdminDashboardPage } from "@/app/pages/admin-dashboard-page"
import { LocalAiLeadPage } from "@/app/pages/local-ai-lead-page"
import { AgentsPage } from "@/app/pages/modules/agents-page"
import { AIExplorerPage } from "@/app/pages/modules/ai-explorer-page"
import { AIParserPage } from "@/app/pages/modules/ai-parser-page"
import { AITranslatorPage } from "@/app/pages/modules/ai-translator-page"
import { DashPage } from "@/app/pages/modules/dash-page"
import { SegmentAnalysisPage } from "@/app/pages/modules/segment-analysis-page"
import { StudioAIPage } from "@/app/pages/modules/studio-ai-page"
import { UseCasesPage } from "@/app/pages/modules/use-cases-page"
import { VideoInsightPage } from "@/app/pages/modules/video-insight-page"
import { ProjectsPage } from "@/app/pages/projects-page"
import { AIAgentsBuildPage } from "@/app/pages/training/ai-agents-build-page"
import { AIAgentsHelloAIPage } from "@/app/pages/training/ai-agents-helloai-page"
import { AIAgentsWhatIsPage } from "@/app/pages/training/ai-agents-what-is-page"
import { AIAwarenessQuizPage } from "@/app/pages/training/ai-awareness-quiz-page"
import { AIExplorerDocumentsPage } from "@/app/pages/training/ai-explorer-documents-page"
import { AIExplorerGettingStartedPage } from "@/app/pages/training/ai-explorer-getting-started-page"
import { AIExplorerPromptsAndProjectsPage } from "@/app/pages/training/ai-explorer-prompts-and-projects-page"
import { AIExplorerSearchAndVerifyPage } from "@/app/pages/training/ai-explorer-search-and-verify-page"
import { AIExplorerWorkingWithContentPage } from "@/app/pages/training/ai-explorer-working-with-content-page"
import { AIHistoryPage } from "@/app/pages/training/ai-history-page"
import { AIParserGettingStartedPage } from "@/app/pages/training/ai-parser-getting-started-page"
import { AIPortfolioGettingStartedPage } from "@/app/pages/training/ai-portfolio-getting-started-page"
import { ChallengesPage } from "@/app/pages/training/challenges-page"
import { ChallengesSummaryPage } from "@/app/pages/training/challenges-summary-page"
import { ConclusionSummaryPage } from "@/app/pages/training/conclusion-summary-page"
import { EUAIActPage } from "@/app/pages/training/eu-ai-act-page"
import { EverydayAIQuizPage } from "@/app/pages/training/everyday-ai-quiz-page"
import { FlowsGettingStartedPage } from "@/app/pages/training/flows-getting-started-page"
import { HelloAISecurityPage } from "@/app/pages/training/hello-ai-security-page"
import { InstallationPage } from "@/app/pages/training/installation-page"
import { IntroductionPage } from "@/app/pages/training/introduction-page"
import { PromptEngineeringPage } from "@/app/pages/training/prompt-engineering-page"
import { RAGPage } from "@/app/pages/training/rag-page"
import { SecurityGeneralPage } from "@/app/pages/training/security-general-page"
import { StudioAIGettingStartedPage } from "@/app/pages/training/studio-ai-getting-started-page"
import { TranslatorIntroductionPage } from "@/app/pages/training/translator-introduction-page"
import { TranslatorTakeawaysPage } from "@/app/pages/training/translator-takeaways-page"
import { TranslatorUseCasesPage } from "@/app/pages/training/translator-use-cases-page"
import { WhatIsAIPage } from "@/app/pages/training/what-is-ai-page"
import { ModuleShell } from "@/components/layouts/module-shell"
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar"
import { PluginsPage } from "@/features/plugins/plugins-page"
import { SlideSensePage } from "@/features/slide-sense/pages/SlideSensePage"
import { TenderLandingPage } from "@/features/tender/components/tender-landing-page"

/** Route id → page component (wrapped). Used by {@link RoutesProvider} when building `routes`. */
export const routePageComponents: { [key: string]: (props: ChatProps) => ReactElement } = {
  plugins: () => (
    <ModuleShell>
      <PluginsPage />
    </ModuleShell>
  ),
  "admin-dashboard": () => (
    <ModuleShell>
      <AdminDashboardPage />
    </ModuleShell>
  ),
  free: () => (
    <SidebarProvider className="relative h-full min-h-0 w-full">
      <AIExplorerPage />
    </SidebarProvider>
  ),
  translate: () => (
    <SidebarProvider className="relative min-h-full">
      <AITranslatorPage />
    </SidebarProvider>
  ),
  "video-insight": () => (
    <SidebarProvider className="relative min-h-full">
      <VideoInsightPage />
    </SidebarProvider>
  ),
  "ai-parser": () => (
    <SidebarProvider className="relative min-h-full">
      <AIParserPage />
    </SidebarProvider>
  ),
  studio_ai: () => (
    <SidebarProvider className="relative min-h-full">
      <StudioAIPage />
    </SidebarProvider>
  ),
  projects: () => (
    <SidebarProvider className="relative min-h-full">
      <ProjectsPage />
    </SidebarProvider>
  ),
  dash: () => (
    <SidebarProvider className="relative min-h-full">
      <DashPage />
    </SidebarProvider>
  ),
  // Hosted agents roster (HERMES). The /agents/:agentSlug detail route is
  // registered in RoutesProvider alongside the other module sub-routes.
  agents: () => (
    <SidebarProvider className="relative min-h-full w-full">
      <SidebarInset className="w-full">
        <AgentsPage />
      </SidebarInset>
    </SidebarProvider>
  ),
  "ai-portfolio": () => (
    <ModuleShell>
      <UseCasesPage />
    </ModuleShell>
  ),
  "slide-sense": () => (
    <SidebarProvider className="relative min-h-full">
      <SlideSensePage />
    </SidebarProvider>
  ),
  "segment-analysis": () => (
    <ModuleShell>
      <SegmentAnalysisPage />
    </ModuleShell>
  ),
  tender: () => (
    <SidebarProvider className="relative min-h-full w-full">
      <SidebarInset className="w-full">
        <TenderLandingPage />
      </SidebarInset>
    </SidebarProvider>
  ),
  // Key preserved as "local-ai-ambassador" because it's persisted as the
  // route's ``component`` value in Cosmos config. Only the imported component
  // was renamed when rebranding to "Local AI Lead".
  "local-ai-ambassador": () => (
    <ModuleShell>
      <LocalAiLeadPage />
    </ModuleShell>
  ),
}

/** Training section component id → element. Used when resolving training module sections. */
export const trainingSectionComponents: { [key: string]: ReactElement | undefined } = {
  "introduction.intro": <IntroductionPage />,
  "introduction.what-is-ai": <WhatIsAIPage />,
  "introduction.ai-history": <AIHistoryPage />,
  "introduction.installation": <InstallationPage />,
  "security.general": <SecurityGeneralPage />,
  "security.risk": <HelloAISecurityPage />,
  "security.eu-ai-act": <EUAIActPage />,
  "challenges.challenges": <ChallengesPage />,
  "challenges.rag": <RAGPage />,
  "challenges.prompting": <PromptEngineeringPage />,
  "challenges.summary": <ChallengesSummaryPage />,
  "ai-agents.what-is": <AIAgentsWhatIsPage />,
  "ai-agents.build": <AIAgentsBuildPage />,
  "ai-agents.helloai": <AIAgentsHelloAIPage />,
  "ai-explorer.getting-started": <AIExplorerGettingStartedPage />,
  "ai-explorer.working-with-content": <AIExplorerWorkingWithContentPage />,
  "ai-explorer.search-and-verify": <AIExplorerSearchAndVerifyPage />,
  "ai-explorer.documents": <AIExplorerDocumentsPage />,
  "ai-explorer.prompts-and-projects": <AIExplorerPromptsAndProjectsPage />,
  "flows.getting-started": <FlowsGettingStartedPage />,
  "ai-parser.getting-started": <AIParserGettingStartedPage />,
  "studio-ai.getting-started": <StudioAIGettingStartedPage />,
  "ai-portfolio.getting-started": <AIPortfolioGettingStartedPage />,
  "ai-translator.introduction": <TranslatorIntroductionPage />,
  "ai-translator.usage": <TranslatorUseCasesPage />,
  "ai-translator.takeaways": <TranslatorTakeawaysPage />,
  "conclusion.summary": <ConclusionSummaryPage />,
  "ai-agents.quiz-awareness": <AIAwarenessQuizPage />,
  "conclusion.quiz-everyday": <EverydayAIQuizPage />,
}
